<template>
  <OverlayLoader :visible="isLoading" />
  <div class="card-body p-4 text-center">
    <div
      class="m-auto m-1230"
      style="max-width: 550px"
      v-if="viewState === 'invalid'"
    >
      <div class="card bg-white border-0 rounded-10 mb-4 text-center">
        <div class="card-body p-4">
          <h4 class="fs-20 fw-semibold mb-2 text-danger">Error !</h4>
          <p class="mb-4 text-danger">{{ message }}</p>
          <RouterLink to="/login" class="btn btn-link w-100 mt-2">
            Ir Login
          </RouterLink>
        </div>
      </div>
    </div>
    <div v-else-if="viewState === 'valid'">
      <div class="d-flex justify-content-center align-items-center vh-100">
        <div
          class="card bg-white border-0 rounded-10 shadow"
          style="width: 400px"
        >
          <div class="card-body p-4 text-center">
            <div class="mb-3">
              <img src="/favicon-32x32.png" class="rounded-3" alt="logo" />
            </div>

            <h4 class="fs-20 fw-semibold mb-2">Recuperar contraseña</h4>

            <p class="mb-4">Ingresa tu nueva contraseña</p>

            <form @submit.prevent="validateForm">
              <div class="mb-3 text-start">
                <label class="form-label">Nueva Contraseña</label>
                <input
                  type="password"
                  class="form-control"
                  v-model.trim="userPasswordRecover.newPassword"
                />
                <div class="input-error">{{ errors.newPassword }}</div>
              </div>
              <div class="mb-3 text-start">
                <label class="form-label">Confirmar contraseña</label>
                <input
                  type="password"
                  class="form-control"
                  v-model.trim="userPasswordRecover.confirmPassword"
                />
                <div class="input-error">{{ errors.confirmPassword }}</div>
              </div>

              <button class="btn btn-primary w-100" :disabled="isLoading">
                Confirmar
              </button>
            </form>
            <p v-if="message" class="text-danger mt-2">{{ message }}</p>
            <RouterLink to="/login" class="btn btn-link w-100 mt-2">
              Ir Login
            </RouterLink>
          </div>
        </div>
      </div>
    </div>
    <div
      class="m-auto m-1230"
      style="max-width: 550px"
      v-else-if="viewState === 'success'"
    >
      <div class="card bg-white border-0 rounded-10 mb-4 text-center">
        <div class="card-body p-4">
          <h4 class="fs-20 fw-semibold mb-2 text-success">Éxito !</h4>
          <p class="mb-4 text-success">Tu contraseña ha sido actualizada</p>
          <RouterLink to="/login" class="btn btn-link w-100 mt-2">
            Ir Login
          </RouterLink>
        </div>
      </div>
    </div>
  </div>
</template>
<script lang="ts">
import { defineComponent, ref, onMounted } from "vue";
import OverlayLoader from "@/components/Others/OverlayLoader.vue";
import { useRoute } from "vue-router";
import api from "@/services/api";
import { userRecoveryPasswordConfirm } from "@/schemas/userRecoveryPasswordConfirm";

export default defineComponent({
  name: "PasswordRecover",
  components: {
    OverlayLoader,
  },
  setup() {
    const route = useRoute();
    const isLoading = ref(false);
    const viewState = ref("loading");

    const message = ref("");
    const userPasswordRecover = ref<{
      newPassword: string;
      confirmPassword: string;
    }>({
      newPassword: "",
      confirmPassword: "",
    });
    const errors = ref<Record<string, string>>({});

    onMounted(async () => {
      try {
        var key = route.query.key;
        if (key) {
          await getValidateChangeUsername(key);
        } else {
          viewState.value = "invalid";
          message.value = "El link de verificación no es válido";
        }
      } catch (error) {
        viewState.value = "invalid";
        message.value = "El link de verificación no es válido";
      }
    });

    const getValidateChangeUsername = async (key: any) => {
      try {
        isLoading.value = true;
        const response = await api.get(
          `/user/validate-recovery-key?key=${key}`
        );
        if (response.data > 0) {
          viewState.value = "valid";
        } else {
          viewState.value = "invalid";
          message.value = "El link de verificación no es válido";
        }
        isLoading.value = false;
      } catch (ex: any) {
        console.error(ex);
        let mensaje = "Error interno";
        if (ex.response?.data) {
          const raw = ex.response.data;
          if (typeof raw === "string") {
            const match = raw.match(/System\.Exception:\s*(.*)/);
            mensaje = match ? match[1] : "Error interno";
          }
        }
        viewState.value = "invalid";
        message.value = mensaje;
      } finally {
        isLoading.value = false;
      }
    };
    const validateForm = async () => {
      try {
        errors.value = {};
        await userRecoveryPasswordConfirm.validate(userPasswordRecover.value, {
          abortEarly: false,
        });
        isLoading.value = true;

        await recoveryPassword();
        isLoading.value = false;
      } catch (err: any) {
        console.log("err: " + err);
        if (err.inner) {
          err.inner.forEach((e: any) => {
            errors.value[e.path] = e.message;
          });
        }
        isLoading.value = false;
      }
    };
    const recoveryPassword = async () => {
      try {
        var jData = {
          newPassword: userPasswordRecover.value.newPassword,
          recoveryCode: route.query.key,
        };
        const response: any = await api.post(`/user/password-recovery`, jData);

        if (response.data > 0) {
          userPasswordRecover.value.newPassword = "";
          userPasswordRecover.value.confirmPassword = "";
          viewState.value = "success";
        }else{
          message.value = "Error interno";
        }
      } catch (ex: any) {
        console.error(ex);
        let mensaje = "Error interno";
        if (ex.response?.data) {
          const raw = ex.response.data;
          if (typeof raw === "string") {
            const match = raw.match(/System\.Exception:\s*(.*)/);
            mensaje = match ? match[1] : "Error interno";
          }
        }
        message.value = mensaje;
        isLoading.value = false;
      }
    };
    const clearError = (field: string) => {
      errors.value[field] = "";
    };
    return {
      isLoading,
      OverlayLoader,
      userPasswordRecover,
      errors,
      viewState,
      message,
      validateForm,
      clearError,
      recoveryPassword,
    };
  },
});
</script>
